import { FC, useEffect, useMemo, useRef, useState } from 'react';
import { captureException } from '@sentry/react';
import { Button, Col, Divider, Input, InputRef, Modal, Row } from 'antd';
import { TbArrowRight, TbLayout } from 'react-icons/tb';
import { useNavigate } from 'react-router-dom';

import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';
import { ResourceDraft, useDirtyResourceDraft } from '@/hooks/useResourceDrafts';
import { useSubmitShortcut } from '@/hooks/useSubmitShortcut';
import { useWorkspaceResourceCreateAccess } from '@/hooks/useWorkspaceResourceCreateAccess';
import { LearnMoreFooter } from '@/shared';
import { toast } from '@/shared/components/toasts/PlatformToast';
import { useDashboardStore } from '@/stores';
import { Dashboard, DashboardVisibility, SetStateMethod } from '@/types';

import CreateMoreControl from '../components/CreateMoreControl';
import ResourceCreationModalTitle from '../components/ResourceCreationModalTitle';
import DashboardTemplates from './layout/DashboardTemplates';

export const MAX_TITLE_LENGTH = 64;

interface DashboardDraftValues {
    description?: string;
    selectedTemplate?: string;
    title?: string;
    visibility?: string;
}

const parseDashboardDraftValues = (value: unknown): DashboardDraftValues => {
    if (!value || typeof value !== 'object' || Array.isArray(value)) {
        return {};
    }

    const draft = value as Record<string, unknown>;

    return {
        description: typeof draft.description === 'string' ? draft.description : undefined,
        selectedTemplate: typeof draft.selectedTemplate === 'string' ? draft.selectedTemplate : undefined,
        title: typeof draft.title === 'string' ? draft.title : undefined,
        visibility: typeof draft.visibility === 'string' ? draft.visibility : undefined,
    };
};

interface CreateDashboardModalProps {
    open: boolean;
    setOpen: SetStateMethod<boolean>;
    initialDraft?: ResourceDraft<unknown> | null;
    onDraftResolved?: () => void;
}

const CreateDashboardModal: FC<CreateDashboardModalProps> = ({
    open,
    setOpen,
    initialDraft,
    onDraftResolved,
}): JSX.Element => {
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        setLoading(false);
    }, []);

    // Autofocus
    const titleInputRef = useRef<InputRef>(null);

    useEffect(() => {
        if (open) {
            setTimeout(() => titleInputRef.current?.focus(), 125);
        }
    }, [open]);

    // Dashboard Metadata
    const [title, setTitle] = useState('');
    const [description, setDescription] = useState('');
    const [visibility, setVisibility] = useState('Private');
    const [submitting, setSubmitting] = useState(false);
    const [createMoreEnabled, setCreateMoreEnabled] = useState<boolean>(false);

    const { token } = useAntDesignTheme();

    const normalizedTitle = useMemo(() => title.trim(), [title]);

    const titleError = useMemo(() => {
        if (!normalizedTitle) {
            return 'Dashboard Name is Required';
        }

        if (normalizedTitle.length > MAX_TITLE_LENGTH) {
            return `Must be ${MAX_TITLE_LENGTH} characters or fewer`;
        }
        return null;
    }, [normalizedTitle]);

    const canSubmit = !submitting && !titleError;

    const navigate = useNavigate();

    const { createDashboard, dashboards } = useDashboardStore();
    const createAccess = useWorkspaceResourceCreateAccess('dashboards', dashboards?.length ?? 0);

    const [selectedTemplate, setSelectedTemplate] = useState('empty');

    const resetForm = () => {
        setTitle('');
        setDescription('');
        setVisibility('Private');
        setSelectedTemplate('empty');
    };

    const { dirtyDraftModal, markSubmitted, requestClose } = useDirtyResourceDraft({
        close: () => setOpen(false),
        draftId: initialDraft?.id,
        isDirty:
            title.trim() !== '' ||
            description.trim() !== '' ||
            visibility !== 'Private' ||
            selectedTemplate !== 'empty',
        reset: resetForm,
        resourceLabel: 'Dashboard',
        resourceType: 'dashboard',
        title,
        values: {
            description,
            selectedTemplate,
            title,
            visibility,
        },
    });

    const handleCreateDashboard = async () => {
        if (!canSubmit || createAccess.disabled) {
            return;
        }

        setSubmitting(true);

        try {
            const payload: Partial<Dashboard> = {
                name: normalizedTitle,
                visibility: visibility as DashboardVisibility,
            };

            const db = await createDashboard(payload, {
                params: { template: selectedTemplate ?? undefined },
            });

            toast('Dashboard Created', {
                icon: <TbLayout size='1rem' />,
                description: db?.name,
            });

            markSubmitted();
            onDraftResolved?.();
            resetForm();

            if (!createMoreEnabled) {
                setOpen(false);

                navigate(`/dashboards/${db?.id}`);
            } else {
                titleInputRef.current?.focus();
            }
        } catch (error) {
            captureException(error);

            toast.error('Failed to Create Dashboard', {
                description: 'Please try again or contact support.',
            });
        } finally {
            setSubmitting(false);
        }
    };

    useSubmitShortcut(open && canSubmit && !createAccess.disabled, handleCreateDashboard, [
        createAccess.disabled,
        normalizedTitle,
        selectedTemplate,
        createMoreEnabled,
        visibility,
    ]);

    useEffect(() => {
        setLoading(false);
    }, []);

    useEffect(() => {
        if (open && initialDraft?.values) {
            const draft = parseDashboardDraftValues(initialDraft.values);

            setTitle(draft.title ?? '');
            setDescription(draft.description ?? '');
            setVisibility(draft.visibility ?? 'Private');
            setSelectedTemplate(draft.selectedTemplate ?? 'empty');
        }
    }, [open, initialDraft]);

    return (
        <>
            <Modal
                open={open}
                title={
                    <ResourceCreationModalTitle
                        icon={<TbLayout size='1rem' style={{ verticalAlign: '-0.175rem' }} />}
                        title='Create Dashboard'
                        visibility={visibility}
                        setVisibility={setVisibility}
                    />
                }
                mask={false}
                style={{ minWidth: '50rem' }}
                styles={{ body: { padding: 0 } }}
                destroyOnHidden
                onCancel={requestClose}
                footer={
                    <Row align={'middle'}>
                        <Col flex='none'>
                            <LearnMoreFooter align='left' topic='Dashboards' />
                        </Col>
                        <Col flex='auto' />
                        <Col flex='none'>
                            <CreateMoreControl
                                checked={createMoreEnabled}
                                disabled={createAccess.disabled}
                                onChange={setCreateMoreEnabled}
                            />
                        </Col>
                        <Col flex='none'>
                            <Button
                                type='primary'
                                onClick={handleCreateDashboard}
                                disabled={loading || !canSubmit || createAccess.disabled}
                                loading={loading || submitting}
                            >
                                Create Dashboard
                                <TbArrowRight size='1rem' style={{ verticalAlign: '-0.175rem' }} />
                            </Button>
                        </Col>
                    </Row>
                }
            >
                <div
                    style={{
                        borderTop: `1px solid ${token.colorBorder}`,
                        borderBottom: `1px solid ${token.colorBorder}`,
                        marginTop: '1rem',
                        paddingTop: '0.5rem',
                        paddingBottom: '0.5rem',
                    }}
                >
                    <Row gutter={[8, 0]}>
                        <Col flex='none'>
                            <div style={{ marginBottom: '0.5rem' }}>
                                <small style={{ color: token.colorTextTertiary }}>Name</small>
                            </div>
                        </Col>
                        <Col flex='auto' />
                    </Row>
                    <Row gutter={[8, 0]}>
                        <Col flex={'auto'}>
                            <Input
                                ref={titleInputRef}
                                addonBefore={
                                    <div style={{ margin: '0 -0.25rem' }}>
                                        <TbLayout
                                            size='1rem'
                                            style={{ verticalAlign: '-0.175rem', color: token.colorTextQuaternary }}
                                        />
                                    </div>
                                }
                                value={title}
                                onChange={(e) => setTitle(e.target.value)}
                                placeholder='Dashboard Name...'
                            />
                        </Col>
                    </Row>
                    <Divider style={{ marginTop: '1rem', marginBottom: 0 }} />
                    <DashboardTemplates template={selectedTemplate} setTemplate={setSelectedTemplate} />
                </div>
            </Modal>
            {dirtyDraftModal}
        </>
    );
};

export default CreateDashboardModal;
